import { UseQueryOptions, useQuery } from "@tanstack/react-query";
import { yearMonth } from "../helpers/dateHelpers";
import {
  APSummary,
  CongAnalysis,
  CongAverages,
  GroupAverage,
  MissingReport,
  MonthDetail,
  MonthReportAllTotal,
  MonthStatistics,
  MonthSummary,
  RPSummary,
  SubmitMonthRequest,
} from "../types/summary";
import { api, fsReportPath } from "./api";
import QueryKeys from "./queryKeys";

class SummaryAPI {
  static path = fsReportPath("/summary");

  static urlForSubmit(year: number, month: number): string {
    return `${SummaryAPI.path}/month/submit/${year}/${month}`;
  }

  static urlForMonth(path: string, ym?: string): string {
    let url = `${SummaryAPI.path}/${path}`;
    if (ym) {
      const [year, month] = yearMonth(ym);
      url += `/${year}/${month}`;
    }
    return url;
  }

  async getMissing(): Promise<MissingReport> {
    const url = `${SummaryAPI.path}/missing`;
    return api.get(url).json();
  }

  async getMonthSummary(ym?: string): Promise<MonthSummary> {
    const url = SummaryAPI.urlForMonth("month", ym);
    return api.get(url).json();
  }

  async getMonthDetail(ym?: string): Promise<MonthDetail> {
    const url = SummaryAPI.urlForMonth("monthdetail", ym);
    return api.get(url).json();
  }

  async getMonthStatistics(ym?: string): Promise<MonthStatistics> {
    const url = SummaryAPI.urlForMonth("statistics", ym);
    return api.get(url).json();
  }

  async getRegPio(year?: number): Promise<RPSummary[]> {
    let url = `${SummaryAPI.path}/regularpioneers`;
    if (year) url += `/svcYear/${year}`;
    return api.get(url).json();
  }

  async getAuxPio(ym?: string): Promise<APSummary> {
    const url = SummaryAPI.urlForMonth("auxpioneers", ym);
    return api.get(url).json();
  }

  async getCongAverages(start?: string, end?: string): Promise<CongAverages> {
    let url = `${SummaryAPI.path}/congavg`;
    if (start && end) {
      const [startYear, startMonth] = yearMonth(start);
      const [endYear, endMonth] = yearMonth(end);
      url += `/${startYear}/${startMonth}/${endYear}/${endMonth}`;
    }

    return api.get(url).json();
  }

  async getSvcYearTotals(year: number): Promise<MonthReportAllTotal[]> {
    const url = `${SummaryAPI.path}/serviceyeartotals/${year}`;
    return api.get(url).json();
  }

  async getCongAnalysis(): Promise<CongAnalysis> {
    //the back end does support querying for different service years, but we're not exposing that for now
    const url = `${SummaryAPI.path}/analysis`;
    return api.get(url).json();
  }

  async getGroupAverage(groupId: number, start?: string, end?: string): Promise<GroupAverage> {
    let url = `${SummaryAPI.path}/groupavg/${groupId}`;
    if (start && end) {
      const [startYear, startMonth] = yearMonth(start);
      const [endYear, endMonth] = yearMonth(end);
      url += `/${startYear}/${startMonth}/${endYear}/${endMonth}`;
    }

    return api.get(url).json();
  }

  async unSubmitMonth(year: number, month: number) {
    await api.delete(SummaryAPI.urlForSubmit(year, month)).json();
  }

  async submitMonth(year: number, month: number, req: SubmitMonthRequest): Promise<MonthSummary> {
    return api.post(SummaryAPI.urlForSubmit(year, month), { json: req }).json();
  }
}

export function useServiceYearTotalsQuery(svcYear: number, options?: UseQueryOptions<MonthReportAllTotal[], Error>) {
  return useQuery({
    queryKey: [QueryKeys.ServiceYearTotals, svcYear],
    queryFn: () => summaryApi.getSvcYearTotals(svcYear),
    ...options,
  });
}

export const summaryApi = new SummaryAPI();
